import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  BookmarkCheck, Crown, Users, CreditCard, User as UserIcon, LogOut, Sparkles,
  HelpCircle, Info, LayoutDashboard, Package, Menu, X, ChevronDown, Gavel, Wallet, Film, LifeBuoy,
  PenLine, FileText, Mic, TrendingUp, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import { createPortal } from 'react-dom';
import { useAuth } from '@/context/AuthContext';
import { useRegion } from '@/context/RegionContext';
import Logo from '@/components/Logo';

/* Feature list shown in the guest "Features" dropdown. Hash links scroll to
   the relevant homepage section (HomePage handles the smooth scroll); the
   violation wizard has its own public page. */
const GUEST_FEATURES = [
  { label: 'Viral script writer', desc: 'Turn any product into hooks & a script', href: '/#toolkit', icon: PenLine },
  { label: 'Video transcription', desc: 'Transcribe any TikTok or Reel instantly', href: '/#transcriber', icon: FileText },
  { label: 'AI voiceovers', desc: 'Generate a voiceover for your script', href: '/#voiceover', icon: Mic },
  { label: 'Trending product research', desc: 'See the best-selling products right now', href: '/#top-products', icon: TrendingUp },
  { label: 'Income graphs', desc: 'Your real monthly earnings at a glance', href: '/#income-calculator', icon: BarChart3 },
  { label: 'Violation wizard', desc: 'Draft a ready-to-submit appeal, free', to: '/free-violation-appeal', icon: Gavel },
];

/* Lightweight click + hover dropdown for the desktop nav. Uses a render-prop
   trigger so the same component drives both the Features and Account menus. */
const NavDropdown = ({ trigger, children, align = 'left', testid, panelTestid }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openNow = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const closeSoon = () => { closeTimer.current = setTimeout(() => setOpen(false), 140); };

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
      data-testid={testid}
    >
      {trigger({ open, toggle: openNow })}
      {open && (
        <div
          className={`absolute top-full mt-2 z-[200] ${align === 'right' ? 'right-0' : 'left-0'}`}
          data-testid={panelTestid}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
};

/**
 * Unified responsive top nav for Poettik.
 *
 * Desktop: black bar with Logo · [Affiliate CTA + Theme + dropdown menu + Generate CTA].
 * Mobile : black bar with Logo · [Earn pill (guests only) + Theme + burger panel].
 *
 * The dropdown / burger panel both consume the same auth-aware `links` array
 * so behaviour stays in sync. `extraLinks` lets a page slot extra items in.
 */
const AppNav = ({ extraLinks = [] }) => {
  const { isAuthenticated, isPro, user, logout } = useAuth();
  const { region } = useRegion();
  const navigate = useNavigate();
  // Carry the visitor's current region (set by /us, /uk, or the toggle) into
  // the sign-up CTA so their pricing currency stays consistent through the
  // funnel.
  const registerHref = region === 'us' ? '/register?region=us' : '/register?region=uk';
  const [open, setOpen] = useState(false);            // mobile slide-in
  const [desktopOpen, setDesktopOpen] = useState(false); // desktop dropdown
  const [desktopAnchor, setDesktopAnchor] = useState({ top: 0, right: 0 });
  const desktopRef = useRef(null);
  const desktopBtnRef = useRef(null);
  const desktopPanelRef = useRef(null);

  // Close menus on outside click / Escape
  useEffect(() => {
    if (!desktopOpen) return undefined;
    const handler = (e) => {
      const insideBtn = desktopBtnRef.current && desktopBtnRef.current.contains(e.target);
      const insidePanel = desktopPanelRef.current && desktopPanelRef.current.contains(e.target);
      if (!insideBtn && !insidePanel) setDesktopOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setDesktopOpen(false);
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [desktopOpen]);

  // Track the toggle button's position so the portalled dropdown stays anchored
  // to it while scrolling / resizing.
  useEffect(() => {
    if (!desktopOpen) return undefined;
    const updateAnchor = () => {
      if (!desktopBtnRef.current) return;
      const rect = desktopBtnRef.current.getBoundingClientRect();
      setDesktopAnchor({
        top: rect.bottom + 8,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    };
    updateAnchor();
    window.addEventListener('resize', updateAnchor);
    window.addEventListener('scroll', updateAnchor, true);
    return () => {
      window.removeEventListener('resize', updateAnchor);
      window.removeEventListener('scroll', updateAnchor, true);
    };
  }, [desktopOpen]);

  // Lock body scroll while mobile menu is open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const handleLogout = () => {
    logout();
    toast.success('Logged out');
    navigate('/');
  };

  // Smart affiliate route — sign-up page for non-affiliates, dashboard otherwise
  const affiliateRoute = isAuthenticated
    ? (user?.is_affiliate ? '/affiliate-dashboard' : '/affiliate-interest')
    : '/affiliate-interest';

  const authedLinks = [
    { to: '/creator-dashboard', label: 'Dashboard', icon: <LayoutDashboard className="w-4 h-4" /> },
    { to: '/dashboard', label: 'Products', icon: <Package className="w-4 h-4" /> },
    { to: '/videos', label: 'Videos', icon: <Film className="w-4 h-4" /> },
    { to: '/violation-wizard', label: 'Violation wizard', icon: <Gavel className="w-4 h-4" /> },
    { to: '/income-calculator', label: 'Income calculator', icon: <Wallet className="w-4 h-4" /> },
    { to: '/help', label: 'Help & support', icon: <LifeBuoy className="w-4 h-4" /> },
    { to: '/saved', label: 'Saved Ideas', icon: <BookmarkCheck className="w-4 h-4" /> },
    { to: '/profile', label: 'Settings', icon: <UserIcon className="w-4 h-4" /> },
    { to: '/subscription', label: 'Subscription', icon: <CreditCard className="w-4 h-4" /> },
    { to: affiliateRoute, label: user?.is_affiliate ? 'Affiliate Dashboard' : 'Earn with Poettik', icon: <Users className="w-4 h-4" /> },
    { to: '/faq', label: 'FAQ', icon: <HelpCircle className="w-4 h-4" /> },
    { to: '/about', label: 'About', icon: <Info className="w-4 h-4" /> },
    ...(!isPro ? [{ to: '/pricing', label: 'Upgrade', icon: <Crown className="w-4 h-4" /> }] : []),
    { to: '#logout', label: 'Log out', icon: <LogOut className="w-4 h-4" />, onClick: handleLogout },
  ];

  const guestLinks = [
    ...GUEST_FEATURES.map((f) => ({ to: f.to || f.href, label: f.label, icon: <f.icon className="w-4 h-4" /> })),
    { to: '/pricing', label: 'Pricing', icon: <CreditCard className="w-4 h-4" /> },
    { to: '/affiliate-interest', label: 'Earn with Poettik', icon: <Users className="w-4 h-4" /> },
    { to: '/faq', label: 'FAQ', icon: <HelpCircle className="w-4 h-4" /> },
    { to: '/about', label: 'About', icon: <Info className="w-4 h-4" /> },
    { to: '/support', label: 'Support', icon: <LifeBuoy className="w-4 h-4" /> },
    { to: '/login', label: 'Log in', icon: <UserIcon className="w-4 h-4" /> },
  ];

  const links = [...extraLinks, ...(isAuthenticated ? authedLinks : guestLinks)];

  // Primary CTA — Generate for authenticated users, Start Free Trial for guests
  // Suffix differentiates desktop vs mobile so data-testids remain unique.
  const renderPrimaryCta = (suffix) => (isAuthenticated ? (
    <Link to="/generator" data-testid={`nav-cta-generate-${suffix}`}>
      <Button variant="default" size="sm">
        <Sparkles className="w-4 h-4 mr-1.5" />
        Generate
      </Button>
    </Link>
  ) : (
    <Link to={registerHref} data-testid={`nav-cta-trial-${suffix}`}>
      <Button variant="default" size="sm">Start Free Trial</Button>
    </Link>
  ));

  // Affiliate sign-up CTA stays visible on desktop (outside the dropdown).
  // Guests + non-affiliate users see it. Existing affiliates have a "Dashboard" inside the dropdown.
  const showAffiliateCta = !isAuthenticated || !user?.is_affiliate;
  const affiliateCta = showAffiliateCta ? (
    <Link to="/affiliate-interest" data-testid="nav-cta-affiliate">
      <Button variant="outline" size="sm">
        <Users className="w-4 h-4 mr-1.5" />
        Earn with Poettik
      </Button>
    </Link>
  ) : null;

  // Mobile-only inline "Earn" pill (compact) for guests
  const mobileEarnPill = !isAuthenticated ? (
    <Link to="/affiliate-interest" data-testid="mobile-earn-pill">
      <Button variant="outline" size="sm" className="h-8 px-2.5 text-xs gap-1.5">
        <Users className="w-3.5 h-3.5" />
        Earn
      </Button>
    </Link>
  ) : null;

  const renderMobileLink = (l, i) => {
    const handleClick = (e) => {
      if (l.onClick) { e.preventDefault(); l.onClick(); }
      setOpen(false);
    };
    return (
      <Link
        key={i}
        to={l.to || '#'}
        onClick={handleClick}
        className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 active:bg-muted transition-colors"
        data-testid={`mobile-menu-link-${(l.label || '').toLowerCase().replace(/\s+/g, '-')}`}
      >
        <span className="w-4 h-4 flex items-center justify-center shrink-0">{l.icon}</span>
        <span>{l.label}</span>
      </Link>
    );
  };

  const renderDesktopLink = (l, i) => {
    const handleClick = (e) => {
      if (l.onClick) { e.preventDefault(); l.onClick(); }
      setDesktopOpen(false);
    };
    return (
      <Link
        key={i}
        to={l.to || '#'}
        onClick={handleClick}
        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground hover:bg-muted transition-colors"
        data-testid={`desktop-menu-link-${(l.label || '').toLowerCase().replace(/\s+/g, '-')}`}
      >
        <span className="w-4 h-4 flex items-center justify-center shrink-0">{l.icon}</span>
        <span>{l.label}</span>
      </Link>
    );
  };

  return (
    <div className="flex items-center justify-between w-full text-foreground">
      <Link to="/" onClick={() => { setOpen(false); setDesktopOpen(false); }}>
        <Logo />
      </Link>

      {/* Mobile right cluster */}
      <div className="flex md:hidden items-center gap-2">
        {mobileEarnPill}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="p-2 rounded-lg text-foreground hover:bg-muted transition-colors"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Desktop right cluster */}
      <div className="hidden md:flex items-center gap-1">
        {!isAuthenticated ? (
          <>
            <a href="/#how-it-works" className="px-3 py-2 rounded-lg text-sm text-foreground/75 hover:text-foreground hover:bg-muted transition-colors">How it works</a>

            <NavDropdown
              testid="nav-features"
              panelTestid="nav-features-panel"
              trigger={({ open: fOpen, toggle }) => (
                <button
                  type="button"
                  onClick={toggle}
                  className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-foreground/75 hover:text-foreground hover:bg-muted transition-colors"
                  data-testid="nav-features-toggle"
                  aria-expanded={fOpen}
                >
                  Features
                  <ChevronDown className={`w-4 h-4 transition-transform ${fOpen ? 'rotate-180' : ''}`} />
                </button>
              )}
            >
              <div className="w-[320px] rounded-xl border border-border bg-popover shadow-[0_12px_32px_rgba(70,45,20,0.12)] p-2">
                {GUEST_FEATURES.map((f) => {
                  const className = 'flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors';
                  const testid = `nav-feature-${f.label.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`;
                  const inner = (
                    <>
                      <span className="mt-0.5 shrink-0 text-primary"><f.icon className="w-4 h-4" /></span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground leading-tight">{f.label}</span>
                        <span className="block text-xs text-muted-foreground leading-snug mt-0.5">{f.desc}</span>
                      </span>
                    </>
                  );
                  return f.to ? (
                    <Link key={f.label} to={f.to} className={className} data-testid={testid}>{inner}</Link>
                  ) : (
                    <a key={f.label} href={f.href} className={className} data-testid={testid}>{inner}</a>
                  );
                })}
              </div>
            </NavDropdown>

            <Link to="/pricing" className="px-3 py-2 rounded-lg text-sm text-foreground/75 hover:text-foreground hover:bg-muted transition-colors">Pricing</Link>
            <Link to="/affiliate-interest" className="px-3 py-2 rounded-lg text-sm text-foreground/75 hover:text-foreground hover:bg-muted transition-colors" data-testid="nav-earn">Earn with Poettik</Link>

            <NavDropdown
              align="right"
              testid="nav-account"
              panelTestid="nav-account-panel"
              trigger={({ open: aOpen, toggle }) => (
                <button
                  type="button"
                  onClick={toggle}
                  className="ml-1.5 w-9 h-9 rounded-full border border-border flex items-center justify-center text-foreground hover:bg-muted transition-colors"
                  data-testid="nav-account-toggle"
                  aria-label="Account menu"
                  aria-expanded={aOpen}
                >
                  <UserIcon className="w-[18px] h-[18px]" />
                </button>
              )}
            >
              <div className="w-56 rounded-xl border border-border bg-popover shadow-[0_12px_32px_rgba(70,45,20,0.12)] p-2">
                <p className="px-3 pt-1.5 pb-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Account</p>
                <Link to="/login" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground hover:bg-muted transition-colors" data-testid="nav-account-login">
                  <UserIcon className="w-4 h-4 text-muted-foreground" /> Log in
                </Link>
                <Link to={registerHref} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-primary hover:bg-muted transition-colors" data-testid="nav-account-register">
                  <Sparkles className="w-4 h-4" /> Create free account
                </Link>
              </div>
            </NavDropdown>
          </>
        ) : (
          <>
            {affiliateCta}
            <div className="relative" ref={desktopRef}>
              <button
                ref={desktopBtnRef}
                type="button"
                onClick={() => setDesktopOpen((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-foreground hover:bg-muted transition-colors"
                data-testid="desktop-menu-toggle"
                aria-expanded={desktopOpen}
              >
                Menu
                <ChevronDown className={`w-4 h-4 transition-transform ${desktopOpen ? 'rotate-180' : ''}`} />
              </button>
              {desktopOpen && typeof document !== 'undefined' && createPortal(
                <div
                  ref={desktopPanelRef}
                  style={{
                    position: 'fixed',
                    top: desktopAnchor.top,
                    right: desktopAnchor.right,
                    backgroundColor: 'hsl(var(--popover))',
                  }}
                  className="w-64 rounded-xl border border-border shadow-[0_12px_32px_rgba(70,45,20,0.06)] p-2 z-[200] flex flex-col"
                  data-testid="desktop-menu-panel"
                >
                  {links.map(renderDesktopLink)}
                </div>,
                document.body
              )}
            </div>
            {renderPrimaryCta('desktop')}
          </>
        )}
      </div>

      {/* Mobile slide-in (portalled to escape parent stacking contexts) */}
      {typeof document !== 'undefined' && createPortal(
        <>
          <div
            onClick={() => setOpen(false)}
            className={`fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm md:hidden transition-opacity duration-200 ${
              open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
            }`}
            data-testid="mobile-menu-backdrop"
          />
          <aside
            className={`fixed top-0 right-0 z-[101] h-screen w-[min(85vw,20rem)] bg-card border-l border-border shadow-2xl md:hidden transform transition-transform duration-200 ease-out ${
              open ? 'translate-x-0' : 'translate-x-full'
            }`}
            data-testid="mobile-menu-panel"
            aria-hidden={!open}
          >
            <div className="flex h-full flex-col p-5">
              <div className="flex items-center justify-between mb-6">
                <Link to="/" onClick={() => setOpen(false)}>
                  <Logo size="sm" />
                </Link>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="p-2 rounded-lg text-foreground hover:bg-muted/50"
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto flex flex-col gap-1 pb-4">
                {links.map(renderMobileLink)}
              </nav>
              <div className="pt-4 border-t border-border" onClick={() => setOpen(false)}>
                <div className="[&>a]:block [&_button]:w-full">{renderPrimaryCta('mobile')}</div>
              </div>
            </div>
          </aside>
        </>,
        document.body
      )}
    </div>
  );
};

export default AppNav;
